import { FollowButtonOutline, FollowButtonSize } from "@product/scmp-sdk";
import first from "lodash/first";
import take from "lodash/take";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { AuthorHeaderBar } from "~/components/author/author-header-bar";
import type { TooltipProps } from "~/components/follow-button/follow-tooltip";
import type { articleAuthorBlockArticle$key } from "~/queries/__generated__/articleAuthorBlockArticle.graphql";

import { ArticleDate } from "./article-date";
import type { ArticleAuthorBlockContainerProps } from "./styles";
import {
  ArticleAuthorBlockContainer,
  ArticleDateAndLabelContainer,
  AvatarContainer,
  InformationContainer,
  StyledArticleTrustLabel,
  StyledAuthorAvatar,
  StyledAuthorNames,
  StyledEntityFollowButton,
} from "./styles";

export type Props = {
  className?: string;
  firstNthAuthor?: number;
  reference: articleAuthorBlockArticle$key;
  tooltipProps?: TooltipProps;
  variant?: ArticleAuthorBlockContainerProps["$variant"];
  withFollow?: boolean;
};

export const ArticleAuthorBlock: FunctionComponent<Props> = ({
  className,
  firstNthAuthor,
  reference: reference_,
  tooltipProps,
  variant = "light",
  withFollow = true,
}) => {
  const article = useFragment(
    graphql`
      fragment articleAuthorBlockArticle on Article {
        ...articleDateContent
        ...authorNamesArticle
        authors {
          entityId
          ...entityFollowButtonBase
          ...authorAvatarAuthor
        }
      }
    `,
    reference_,
  );

  if (!article) return null;

  const authors = firstNthAuthor ? take(article?.authors, firstNthAuthor) : article?.authors;

  const authorsLength = authors?.length ?? 0;

  const handleRenderFollowButton = () => {
    const firstAuthor = first(article?.authors);
    if (!withFollow || !firstAuthor || authorsLength > 1) return null;
    return (
      <StyledEntityFollowButton
        outline={FollowButtonOutline.Outlined}
        reference={firstAuthor}
        size={FollowButtonSize.Thin}
        tooltipProps={tooltipProps}
      />
    );
  };

  return (
    <ArticleAuthorBlockContainer
      $authorsLength={authorsLength}
      $variant={variant}
      className={className}
    >
      <AvatarContainer>
        {authors?.map(
          (author, index) =>
            author &&
            index < 5 && (
              <StyledAuthorAvatar
                $zIndex={authorsLength - index}
                height={60}
                key={author.entityId}
                reference={author}
                width={60}
              />
            ),
        )}
      </AvatarContainer>

      <InformationContainer $authorsLength={authorsLength}>
        <AuthorHeaderBar />
        <StyledAuthorNames firstNthAuthor={firstNthAuthor} reference={article}>
          {handleRenderFollowButton()}
        </StyledAuthorNames>
        <ArticleDateAndLabelContainer>
          <ArticleDate reference={article} />
          <StyledArticleTrustLabel />
        </ArticleDateAndLabelContainer>
      </InformationContainer>
    </ArticleAuthorBlockContainer>
  );
};

ArticleAuthorBlock.displayName = "ArticleAuthorBlock";
